import axios from "axios";

const SAVY_API_BASE = "https://api.savyapp.dev/api/v1";
export const REFUND_FLOW_ID = "689b213664d03f7782f0a1c4";
const getAuthHeaders = () => {
  const token = localStorage.getItem("extarrnalToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * Map question validation.target (from API) to refund-estimation payload keys.
 * Backend expects camelCase fields for PATCH body.
 */
export const refundEstimationFieldMap = {
  income_refund: "income",
  travel_for_work_refund: "travelForWork",
  howDoYouMakeYourWorkJourneysRefund: "vehicleType",
  workJourneyTypeRefund: "workJourneyType",
  refundYears: "refundYears",
  foodAndDrinkRefund: "didEmployerPayForFoodAndDrink",
  weeklyFoodAndDrinkSpend: "weeklyFoodAndDrinkSpend",
  repaymentType: "repaymentType",
  mileageRateType: "mileageRateType",
  mileageRate: "mileageRate",
  milesPerYear: "milesPerYear",
};

// Label/option -> API value maps (from listQuestionsRefund xhrParams)
const INCOME_MAP = {
  "Under £14k": "under_14000",
  "Under\n£14k": "under_14000",
  "Between £14k & £50k": "between_14000_50000",
  "Between\n£14k & £50k": "between_14000_50000",
  "Over £50k": "over_50000",
  "Over\n£50k": "over_50000",
};
const VEHICLE_TYPE_MAP = {
  "My vehicle": "my_vehicle",
  "My\nvehicle": "my_vehicle",
  "Company vehicle": "company_vehicle",
  "Company\nvehicle": "company_vehicle",
  Train: "train",
};
const MILEAGE_RATE_TYPE_MAP = {
  "Mileage rate": "mileage_rate",
  "Fuel card": "fuel_card",
  "No expenses": "no_expenses",
};
const REPAYMENT_TYPE_MAP = {
  "No, I paid for it and didn't get anything back from my employer": "no_repayment",
  "Yes, I paid for it but claimed some of it back from my employer": "some_repayment",
  "Yes, I paid for it and claimed all of it back from my employer": "total_repayment",
  "Yes, I had a company credit card": "company_card",
};

/**
 * Build refund-estimation PATCH payload from user answer, following listQuestionsRefund xhrParams.
 * @param {object} question - current question { ref, type, xhrParams, options }
 * @param {any} userAnswer - single value or array of selected labels/values
 * @param {object} answers - all answers (for proposal e.g. mileage rate)
 * @param {string} validationTarget - from question.xhrParams.validation.target
 * @returns {object} payload for PATCH refund-estimations
 */
export function buildRefundEstimationPayload(question, userAnswer, answers = {}, validationTarget) {
  const ref = question?.ref;
  const payload = { status: "in_progress" };
  const selectedLabels = Array.isArray(userAnswer) ? userAnswer : [userAnswer].filter((v) => v != null && v !== "");

  switch (ref) {
    case "1":
      payload.income = selectedLabels.map((l) => INCOME_MAP[l] || l).filter(Boolean);
      payload.app_status = "screen_2";
      break;
    case "2":
      payload.travelForWork = userAnswer === "Yes";
      payload.app_status = "screen_3";
      break;
    case "3":
      payload.vehicleType = selectedLabels.map((l) => VEHICLE_TYPE_MAP[l] || l).filter(Boolean);
      payload.app_status = "screen_4";
      break;
    case "4":
      payload.app_status = "screen_4";
      break;
    case "5":
      payload.milesPerYear = parseFloat(String(userAnswer).replace(/,/g, "")) || 0;
      payload.app_status = "screen_5";
      break;
    case "6": {
      const rateLabel = userAnswer;
      if (rateLabel === "No expenses") {
        payload.mileageRateType = "no_expenses";
        payload.mileageRate = 0;
      } else if (rateLabel === "Fuel card") {
        payload.mileageRateType = "fuel_card";
        payload.mileageRate = 0;
      } else {
        payload.mileageRateType = "mileage_rate";
        const proposalKey = rateLabel;
        const proposalInputs = question?.proposal?.[proposalKey] || question?.xhrParams?.inputs;
        let val = "";
        if (Array.isArray(proposalInputs) && proposalInputs[0]) {
          const inputId = proposalInputs[0]._id || proposalInputs[0].ref;
          val = answers[inputId] ?? proposalInputs[0].value ?? "";
        }
        if (val === "" && answers["689c6c7b64d03f7782f0a4e8"] != null) {
          val = answers["689c6c7b64d03f7782f0a4e8"];
        }
        let mileageRate = parseFloat(String(val).replace(/,/g, "")) || 0;
        if (mileageRate > 1) mileageRate /= 100;
        payload.mileageRate = mileageRate;
      }
      payload.app_status = "screen_6";
      break;
    }
    case "7":
      payload.app_status = "screen_8";
      break;
    case "8": {
      const label = userAnswer;
      payload.didEmployerPayForFoodAndDrink = label !== "No, I paid for it and didn't get anything back from my employer";
      payload.repaymentType = REPAYMENT_TYPE_MAP[label] || null;
      payload.app_status = "screen_10";
      break;
    }
    case "9":
      payload.weeklyFoodAndDrinkSpend = parseFloat(String(userAnswer).replace(/,/g, "")) || 0;
      payload.app_status = "screen_9";
      break;
    case "11":
      payload.refundYears = Array.isArray(userAnswer) ? userAnswer.length : (userAnswer ? 1 : 0);
      payload.app_status = "screen_11";
      payload.status = "completed";
      break;
    default: {
      const key = refundEstimationFieldMap[validationTarget] || validationTarget;
      if (key) {
        let value = userAnswer;
        if (question?.xhrParams?.validation?.type === "boolean") {
          value = userAnswer === "Yes" || userAnswer === true;
        } else if (Array.isArray(userAnswer)) {
          value = userAnswer.map((l) => {
            const fromIncome = INCOME_MAP[l];
            const fromVehicle = VEHICLE_TYPE_MAP[l];
            if (fromIncome) return fromIncome;
            if (fromVehicle) return fromVehicle;
            return l;
          }).filter(Boolean);
        }
        payload[key] = value;
      }
      if (!payload.app_status && validationTarget) {
        payload.app_status = "screen_1";
      }
      break;
    }
  }
  return payload;
}

/**
 * Create a new refund estimation when user starts the refund flow.
 * @returns {Promise<{ id: string, ... }>}
 */
export async function createRefundEstimation() {
  const payload = {
    status: "in_progress",
    app_status: "screen_1",
  };
  const response = await axios.post(
    `${SAVY_API_BASE}/refund-estimations`,
    payload,
    { headers: getAuthHeaders() }
  );
  return response.data;
}

/**
 * Update refund estimation with answer payload.
 * @param {string} refundEstimationId
 * @param {Record<string, unknown>} payload - partial body (e.g. { income: ["under_14000"], app_status: "screen_2" })
 * @returns {Promise<{ refund?, totalExpenses?, ... }>}
 */
export async function patchRefundEstimation(refundEstimationId, payload) {
  const response = await axios.patch(
    `${SAVY_API_BASE}/refund-estimations/${refundEstimationId}`,
    payload,
    { headers: getAuthHeaders() }
  );
  return response.data;
}
